import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, X, Sparkles, Flame, ShieldAlert } from 'lucide-react';

import bornovaImg from '../assets/tax_bornova_sokagi.jpg';
import eskortImg from '../assets/tax_eskort_fiasco.jpg';
import metresImg from '../assets/tax_kordon_metres.jpg';
import pavyonImg from '../assets/tax_goztepe_pavyon.jpg';
import rusvetImg from '../assets/tax_belediye_rusveti.jpg';
import pirlantaImg from '../assets/tax_urla_pirlanta.jpg';
import alacatiImg from '../assets/tax_alacati_dondurma.jpg';
import sirinceImg from '../assets/tax_sirince_sarap.jpg';
import cesmeRadarImg from '../assets/tax_cesme_radar.jpg';
import basmanePokerImg from '../assets/tax_basmane_poker.jpg';
import pasaportYatImg from '../assets/tax_pasaport_yat.jpg';
import asansorVurgunuImg from '../assets/tax_asansor_vurgunu.jpg';
import vapurKavgaImg from '../assets/tax_vapur_kavga.jpg';

const SCENARIO_IMAGES = {
  bornova_sokagi: bornovaImg,
  eskort_parasi: eskortImg,
  bosanma_tazminati: metresImg,
  goztepe_pavyon: pavyonImg,
  belediye_rusveti: rusvetImg,
  urla_pirlanta: pirlantaImg,
  alacati_dondurma: alacatiImg,
  sirince_sarap: sirinceImg,
  cesme_radar: cesmeRadarImg,
  basmane_poker: basmanePokerImg,
  pasaport_yat: pasaportYatImg,
  asansor_vurgunu: asansorVurgunuImg,
  vapur_kavga: vapurKavgaImg
};

export default function TaxScenarioModal({ scenario, amount, playerName, autoClose = false, onClose }) {
  const [visible, setVisible] = useState(false);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    setVisible(true);
    if (autoClose) {
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(() => {
          if (onCloseRef.current) onCloseRef.current();
        }, 300);
      }, 6000); // 6 seconds auto-close when enabled

      return () => clearTimeout(timer);
    }
  }, [autoClose]);

  if (!scenario) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(5, 7, 15, 0.85)',
        backdropFilter: 'blur(12px)',
        zIndex: 250000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
      }}
    >
      <div
        style={{
          background: 'linear-gradient(145deg, #1e1b4b 0%, #0f172a 60%, #090d16 100%)',
          border: '2px solid rgba(244, 63, 94, 0.6)',
          borderRadius: '24px',
          width: '100%',
          maxWidth: '440px',
          padding: '28px 24px',
          textAlign: 'center',
          boxShadow: '0 25px 50px -12px rgba(244, 63, 94, 0.4), 0 0 30px rgba(244, 63, 94, 0.25)',
          transform: visible ? 'scale(1) translateY(0)' : 'scale(0.85) translateY(20px)',
          transition: 'all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Glow Effects */}
        <div
          style={{
            position: 'absolute',
            top: '-60px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '160px',
            height: '160px',
            background: 'radial-gradient(circle, rgba(244,63,94,0.45) 0%, rgba(244,63,94,0) 70%)',
            pointerEvents: 'none'
          }}
        />

        {/* Close Button */}
        <button
          onClick={() => {
            setVisible(false);
            setTimeout(() => {
              if (onClose) onClose();
            }, 200);
          }}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#94a3b8',
            cursor: 'pointer'
          }}
        >
          <X size={18} />
        </button>

        {/* Header Tagline */}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(244, 63, 94, 0.15)', border: '1px solid rgba(244, 63, 94, 0.3)', padding: '6px 14px', borderRadius: '20px', color: '#fda4af', fontSize: '13px', fontWeight: '700', marginBottom: '16px' }}>
          <ShieldAlert size={15} />
          İZMİR SÜRPRİZ CEZASI
        </div>

        {/* Main Icon / Image Illustration */}
        {SCENARIO_IMAGES[scenario.id] ? (
          <div style={{ width: '100%', height: '180px', borderRadius: '16px', overflow: 'hidden', margin: '8px 0 16px', border: '2px solid rgba(244, 63, 94, 0.4)', boxShadow: '0 8px 20px rgba(0,0,0,0.5)' }}>
            <img src={SCENARIO_IMAGES[scenario.id]} alt={scenario.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        ) : (
          <div style={{ fontSize: '64px', margin: '8px 0 16px', filter: 'drop-shadow(0 10px 15px rgba(244, 63, 94, 0.4))', transform: 'scale(1.1)', transition: 'transform 0.3s ease' }}>
            {scenario.icon || '💸'}
          </div>
        )}

        {/* Scenario Title */}
        <h3 style={{ fontSize: '24px', fontWeight: '900', color: '#ffffff', margin: '0 0 12px', letterSpacing: '-0.5px' }}>
          {scenario.title}
        </h3>

        {/* Player Name */}
        <div style={{ fontSize: '14px', fontWeight: '700', color: '#fbbf24', marginBottom: '12px' }}>
          👤 {playerName || 'Oyuncu'} Paşamız Yandı!
        </div>

        {/* Detailed Scenario Description */}
        <p style={{ fontSize: '15px', color: '#cbd5e1', lineHeight: '1.55', margin: '0 0 20px', background: 'rgba(15, 23, 42, 0.6)', padding: '14px 16px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.06)' }}>
          {scenario.text}
        </p>

        {/* Deducted Amount Badge */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: 'linear-gradient(135deg, #be123c 0%, #9f1239 100%)', color: '#ffffff', padding: '12px 24px', borderRadius: '16px', fontSize: '22px', fontWeight: '900', boxShadow: '0 8px 20px rgba(190, 18, 60, 0.4)' }}>
          <span>Cüzdandan Giden:</span>
          <span style={{ color: '#fecdd3' }}>-₺{amount || 200}</span>
        </div>

        {/* Dismiss Button */}
        <button
          onClick={() => {
            setVisible(false);
            setTimeout(() => {
              if (onClose) onClose();
            }, 200);
          }}
          style={{
            marginTop: '20px',
            width: '100%',
            padding: '12px',
            background: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: '14px',
            color: '#94a3b8',
            fontSize: '14px',
            fontWeight: '700',
            cursor: 'pointer'
          }}
        >
          Eyvallah Ağa, Sağlık Olsun 👌
        </button>
      </div>
    </div>,
    document.body
  );
}
